<script setup lang="ts">
/**
 * Диалоговое окно создания и редактирования порога вероятности нежелательного события.
 *
 * Функционал:
 * - Выбор уровня вероятности (0..4) с фильтрацией уже настроенных порогов при создании;
 * - Ввод порогового значения количества событий (целое неотрицательное число);
 * - Выбор расчетного периода (день, неделя, месяц, квартал, год);
 * - Валидация полей формы и защита от потери несохраненных данных (useDialogDismissGuard / ConfirmDialog);
 * - Сохранение данных через adverseSettingsStore с отображением серверных ошибок.
 */

// Импорты Vue
import { computed, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'

// Импорты UI-компонентов
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'

// Импорты хранилищ
import { useAdverseSettingsStore } from '../../stores/adverseSettingsStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'

// Импорты типов и констант
import {
  PROBABILITY_PERIOD_OPTIONS,
  type IAdverseProbabilityThreshold,
  type TProbabilityPeriod,
} from '../../types/IAdverseSettings'
import { useDialogDismissGuard } from '@/composables/useDialogDismissGuard'

/**
 * Входящие параметры диалога
 */
const props = defineProps<{
  /** Видимость диалогового окна */
  modelValue: boolean
  /** Редактируемый порог (null для режима создания) */
  thresholdItem: IAdverseProbabilityThreshold | null
  /** Список уже занятых уровней вероятности (для исключения дубликатов при создании) */
  occupiedProbabilities: number[]
}>()

/**
 * События компонента
 */
const emit = defineEmits<{
  /** Обновление видимости диалога */
  'update:modelValue': [value: boolean]
  /** Успешное сохранение порога */
  'saved': [item: IAdverseProbabilityThreshold]
}>()

const { smAndDown } = useDisplay()
const adverseSettingsStore = useAdverseSettingsStore()
const feedbackStore = useFeedbackStore()

/**
 * Локальное состояние полей формы
 */
const formData = ref<{
  probability: number | null
  threshold: number | string
  period: TProbabilityPeriod
}>({
  probability: null,
  threshold: '',
  period: 'month',
})

/** Снимок начального состояния формы для отслеживания изменений */
const initialSnapshot = ref('')

/** Флаг диалога предупреждения о несохраненных данных */
const showUnsavedChangesDialog = ref(false)

/** Ошибки валидации полей формы */
const errors = ref<{
  probability?: string
  threshold?: string
  period?: string
}>({})

/** Признак режима редактирования */
const isEditMode = computed(() => !!props.thresholdItem)

/** Заголовок модального окна */
const title = computed(() => isEditMode.value ? 'Редактирование порога вероятности' : 'Добавление порога вероятности')

/**
 * Полный справочник уровней вероятности по стандарту RiskManager
 */
const allProbabilityLevels = [
  { value: 0, label: 'Практически исключено' },
  { value: 1, label: 'Низкая' },
  { value: 2, label: 'Средняя' },
  { value: 3, label: 'Выше средней' },
  { value: 4, label: 'Высокая' },
]

/**
 * Доступные опции уровней вероятности для выпадающего списка
 */
const availableProbabilityOptions = computed(() => {
  if (isEditMode.value && props.thresholdItem) {
    return allProbabilityLevels.filter(
      lvl => lvl.value === props.thresholdItem!.probability || !props.occupiedProbabilities.includes(lvl.value),
    )
  }
  return allProbabilityLevels.filter(lvl => !props.occupiedProbabilities.includes(lvl.value))
})

/**
 * Получить сериализованный снимок текущих данных формы
 *
 * @returns JSON-строка снимка
 */
function getSnapshot(): string {
  return JSON.stringify({
    probability: formData.value.probability,
    threshold: String(formData.value.threshold).trim(),
    period: formData.value.period,
  })
}

/**
 * Проверка наличия несохраненных изменений
 *
 * @returns true, если данные были изменены
 */
function hasChanges(): boolean {
  return getSnapshot() !== initialSnapshot.value
}

/**
 * Сброс и заполнение формы при открытии
 */
watch(
  () => [props.modelValue, props.thresholdItem],
  ([isOpen]) => {
    errors.value = {}
    if (isOpen) {
      if (props.thresholdItem) {
        formData.value = {
          probability: props.thresholdItem.probability,
          threshold: props.thresholdItem.threshold,
          period: props.thresholdItem.period || 'month',
        }
      } else {
        // Выбираем первый доступный незанятый уровень по умолчанию
        const firstAvailable = availableProbabilityOptions.value[0]?.value ?? null
        formData.value = {
          probability: firstAvailable,
          threshold: '',
          period: 'month',
        }
      }
      initialSnapshot.value = getSnapshot()
    }
  },
  { immediate: true },
)

/**
 * Прямое закрытие модального окна без проверок
 */
function closeModalDirectly() {
  emit('update:modelValue', false)
  errors.value = {}
}

/**
 * Проверка несохраненных данных перед закрытием
 */
function handleCloseModal() {
  if (hasChanges()) {
    showUnsavedChangesDialog.value = true
  } else {
    closeModalDirectly()
  }
}

/** Защитный хук от случайного клика вне окна */
const onDialogDismissAttempt = useDialogDismissGuard(handleCloseModal, 'ProbabilityThresholdFormDialog')

/**
 * Обработка ответа в диалоге несохраненных изменений
 *
 * @param confirmed true, если пользователь подтвердил закрытие
 */
function handleCloseConfirm(confirmed: boolean) {
  showUnsavedChangesDialog.value = false
  if (confirmed) {
    closeModalDirectly()
  }
}

/**
 * Валидация полей формы перед отправкой
 *
 * @returns true, если форма валидна
 */
function validate(): boolean {
  errors.value = {}
  let isValid = true

  if (formData.value.probability === null || formData.value.probability === undefined) {
    errors.value.probability = 'Выберите уровень вероятности'
    isValid = false
  }

  const thresholdStr = String(formData.value.threshold).trim()
  if (!thresholdStr) {
    errors.value.threshold = 'Укажите пороговое количество событий'
    isValid = false
  } else {
    const num = Number(thresholdStr)
    if (isNaN(num) || !Number.isInteger(num)) {
      errors.value.threshold = 'Порог должен быть целым числом'
      isValid = false
    } else if (num < 0) {
      errors.value.threshold = 'Пороговое значение не может быть отрицательным'
      isValid = false
    }
  }

  if (!formData.value.period) {
    errors.value.period = 'Выберите расчетный период'
    isValid = false
  }

  return isValid
}

/**
 * Сохранение порога вероятности (создание или обновление)
 */
async function handleSave() {
  if (!validate()) return

  const payload = {
    probability: Number(formData.value.probability),
    threshold: Number(formData.value.threshold),
    period: formData.value.period,
  }

  try {
    let result: IAdverseProbabilityThreshold | null = null
    if (isEditMode.value && props.thresholdItem) {
      result = await adverseSettingsStore.updateProbabilityThreshold(props.thresholdItem.id, payload)
    } else {
      result = await adverseSettingsStore.createProbabilityThreshold(payload)
    }

    if (result) {
      emit('saved', result)
      closeModalDirectly()
    }
  } catch (error) {
    // Ошибка отобразится через showToast в сторе
  }
}
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="560"
    persistent
    @click:outside="onDialogDismissAttempt"
    @keydown.esc.stop="onDialogDismissAttempt"
  >
    <VCard>
      <!-- Шапка диалога -->
      <VCardTitle class="d-flex pa-6" :class="smAndDown ? 'px-6 py-4' : 'pa-6'">
        <div class="form_card d-flex justify-space-between w-100">
          <div class="form_card__title">
            <span class="text-h4">
              {{ title }}
            </span>
          </div>
        </div>
        <CustomCloseButton :close-action="handleCloseModal" />
      </VCardTitle>

      <!-- Содержимое формы -->
      <VCardText>
        <div class="d-flex flex-column gap-5 px-0">
          <!-- Уровень вероятности -->
          <VSelect
            v-model="formData.probability"
            :items="availableProbabilityOptions"
            item-title="label"
            item-value="value"
            variant="outlined"
            persistent-placeholder
            placeholder="Выберите уровень вероятности"
            :error="!!errors.probability"
            :error-messages="errors.probability"
          >
            <template #label>
              <span>Уровень вероятности<sup class="required-label">*</sup></span>
            </template>
          </VSelect>

          <!-- Пороговое значение количества событий -->
          <VTextField
            v-model="formData.threshold"
            type="number"
            min="0"
            step="1"
            placeholder="Например: 5"
            variant="outlined"
            clearable
            persistent-placeholder
            :error="!!errors.threshold"
            :error-messages="errors.threshold"
            @click:clear="formData.threshold = ''"
          >
            <template #label>
              <span>Количество НС одного вида<sup class="required-label">*</sup></span>
            </template>
          </VTextField>

          <!-- Расчетный период -->
          <VSelect
            v-model="formData.period"
            :items="PROBABILITY_PERIOD_OPTIONS"
            item-title="label"
            item-value="value"
            variant="outlined"
            persistent-placeholder
            placeholder="Выберите период"
            :error="!!errors.period"
            :error-messages="errors.period"
          >
            <template #label>
              <span>Расчетный период<sup class="required-label">*</sup></span>
            </template>
          </VSelect>

          <!-- Кнопка сохранения -->
          <div class="d-flex align-center mt-3 justify-end">
            <VBtn
              color="primary"
              :loading="feedbackStore.isGlobalLoading"
              :block="smAndDown"
              @click="handleSave"
            >
              {{ isEditMode ? 'Сохранить' : 'Создать' }}
            </VBtn>
          </div>
        </div>
      </VCardText>
    </VCard>

    <!-- Диалог подтверждения выхода при несохраненных данных -->
    <ConfirmDialog
      v-model:is-dialog-visible="showUnsavedChangesDialog"
      confirmation-question="У вас есть несохраненные изменения. Вы уверены, что хотите закрыть окно?"
      confirm-btn="Закрыть"
      reject-btn="Остаться"
      @confirm="handleCloseConfirm"
    />
  </VDialog>
</template>
